import { brandKitValidator } from "@redotech/redo-content-builder-common/brand-kit";
import { MarketingForgeElementType } from "@redotech/redo-content-builder-common/element-schema";
import * as z from "zod";
import { LayoutType, SignUpFormGoal } from "../signup-form/signup-form";
import {
  type FormStep,
  signupFormSchema,
  type SignupFormWithAnalytics,
} from "../signup-form/signup-form-schema";

export type RcsOptInViewport = "desktop" | "mobile";

export interface RcsOptInSignupForm {
  readonly formId: string;
  /** The storefront refuses a forced form on a device it does not appear on. */
  readonly viewport: RcsOptInViewport;
}

function stepCollectsPhone(step: FormStep): boolean {
  return step.elements.some((element) =>
    element.type === MarketingForgeElementType.COLUMN
      ? element.elements.some(
          (nested) =>
            nested.type === MarketingForgeElementType.INPUT &&
            nested.inputType === "phone",
        )
      : element.type === MarketingForgeElementType.INPUT &&
        element.inputType === "phone",
  );
}

function isPublishedPhonePopup(form: SignupFormWithAnalytics): boolean {
  return (
    form.publishedAt != null &&
    form.form.goal === SignUpFormGoal.MARKETING_SUBSCRIPTION &&
    (form.form.layout.type === LayoutType.POPUP ||
      form.form.layout.type === LayoutType.FULLSCREEN) &&
    form.form.behavior.appearsOnSite === "marketing" &&
    form.form.steps.some(stepCollectsPhone)
  );
}

function viewportOf(form: SignupFormWithAnalytics): RcsOptInViewport {
  return form.form.behavior.appearsOn === "mobile" ? "mobile" : "desktop";
}

/** Desktop fits the whole disclosure in one legible frame, then recency. */
function byEvidenceQuality(
  a: SignupFormWithAnalytics,
  b: SignupFormWithAnalytics,
): number {
  return (
    Number(b.form.behavior.appearsOn !== "mobile") -
      Number(a.form.behavior.appearsOn !== "mobile") ||
    (b.publishedAt?.getTime() ?? 0) - (a.publishedAt?.getTime() ?? 0)
  );
}

/**
 * A form `?signup-form-id=` will really render with a phone step; anything
 * else makes the storefront fall back to a different form.
 */
export function pickRcsOptInSignupForm(
  forms: readonly SignupFormWithAnalytics[],
): RcsOptInSignupForm | undefined {
  const chosen = forms
    // A forced id is ignored on page load for exit-intent forms.
    .filter(
      (form) =>
        isPublishedPhonePopup(form) &&
        !form.form.behavior.display.exitIntent.enabled,
    )
    .sort(byEvidenceQuality)[0];
  return chosen
    ? { formId: chosen._id.toString(), viewport: viewportOf(chosen) }
    : undefined;
}

export interface RcsOptInRenderForm {
  readonly form: SignupFormWithAnalytics;
  readonly phoneStepIndex: number;
  readonly viewport: RcsOptInViewport;
}

/**
 * The live popup to draw at its phone step. Unlike a storefront capture, a
 * render needs no forced id, so an exit-intent popup qualifies.
 */
export function pickRcsOptInRenderForm(
  forms: readonly SignupFormWithAnalytics[],
): RcsOptInRenderForm | undefined {
  const chosen = forms.filter(isPublishedPhonePopup).sort(byEvidenceQuality)[0];
  return chosen
    ? {
        form: chosen,
        phoneStepIndex: chosen.form.steps.findIndex(stepCollectsPhone),
        viewport: viewportOf(chosen),
      }
    : undefined;
}

/** What a client needs to draw the storefront popup exactly as shoppers see it. */
export const rcsOptInRenderSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("ready"),
    form: signupFormSchema,
    phoneStepIndex: z.number().int().nonnegative(),
    viewport: z.enum(["desktop", "mobile"]),
    brandKit: brandKitValidator,
    merchantName: z.string().optional(),
    supportEmail: z.string().optional(),
  }),
  z.object({ status: z.literal("blocked"), detail: z.string() }),
]);
export type RcsOptInRender = z.infer<typeof rcsOptInRenderSchema>;
